/**
 * Base de `core-contracts`: um contrato por rota do core que backend E
 * frontend precisam concordar sobre — rota, método, tipo de params/response,
 * o payload do evento de socket de invalidação, e o nome desse evento.
 *
 * Nasce pequeno de propósito: só entram aqui as rotas que a gente TOCA de
 * novo (ex: ao adicionar cache de leitura no frontend). Não é objetivo
 * cobrir as ~51 rotas do backend de uma vez — ver CLAUDE.md e a memória
 * project_plugins_naming_issues sobre generalizar cedo demais.
 *
 * Hoje resolve só a duplicação de STRING (rota + nome de evento de socket)
 * e o TIPO do response/payload — não é um client HTTP. Cada lado
 * (backend/frontend) continua usando seu próprio Axios/Express; este
 * pacote só garante que os dois apontam pro mesmo lugar com o mesmo shape.
 *
 * `TResponse` é o corpo do GET (rota REST). `TEventPayload` é o que chega
 * via `.emit()` no evento de invalidação — são coisas DIFERENTES (o GET
 * devolve a lista/o registro; o evento costuma trazer só o item que mudou
 * + `action`), por isso dois generics separados em vez de reusar um.
 *
 * Só compila em `.ts` — em `.js` (o frontend inteiro) o tipo é apagado no
 * build (Babel, não tsc) e não é checado; serve só se o dev anotar via
 * JSDoc (`@param {import('@markedesk/core-contracts').QueueEventPayload}`).
 * Ver CLAUDE.md do plugin-sdk, seção 11.
 */

export interface ContractEvents {
  /** O fato principal do domínio: "este dado mudou". */
  changed: string;
  /** Fatos adicionais, quando o domínio tem mais de um. Ex: `session` do whatsapp. */
  [nome: string]: string;
}

/**
 * Decisões de quem CONSOME a leitura como cache. Separado de `events` de
 * propósito: o backend emite um fato ("um contato mudou"), e não sabe — nem
 * precisa saber — que existe um cache do outro lado. O cache é um consumidor
 * entre outros; a tela de Contatos, por exemplo, consome o mesmo evento para
 * atualizar a lista sem passar por cache nenhum.
 */
export interface ContractCache {
  /**
   * Rede de segurança, não mecanismo de correção — quem mantém o dado certo
   * é `invalidatedBy`. A régua para escolher o número NÃO é "com que
   * frequência o dado muda", é quanto dói se alguém esquecer de emitir o
   * evento num caminho de escrita novo (e esquecem: uma auditoria em
   * 08/09/2026 achou 13 lugares assim).
   */
  ttl: number;
  /**
   * Quais fatos derrubam este cache, por NOME (as chaves de `events`).
   *
   * Lista, não um só: a lista de conexões muda tanto por `changed` quanto por
   * `session` — ligar só um deixaria conexão caída aparecendo como conectada.
   * Manter isto ao lado de `events` é o que faz saltar aos olhos quando
   * alguém acrescenta um fato novo e esquece de dizer se ele invalida.
   */
  invalidatedBy: string[];
}

export interface ContractDef<TParams = void, TResponse = unknown, TEventPayload = unknown> {
  /** Caminho da rota, relativo à raiz da API (ex: "/queue"). Sem companyId — o isolamento por tenant é implícito via isAuth/JWT. */
  route: string;
  /** Só para rota com parâmetro no path. Ex: `id => \`/companies/listPlan/${id}\`` */
  buildRoute?: (...args: any[]) => string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  /**
   * Fatos do domínio, como trafegam no socket.
   *
   * Sem companyId no nome: o socket já é namespace por empresa
   * (`io.of(String(companyId))`), então repetir o id na string era redundante
   * — e, pior, mascarava um `.of()` esquecido (o evento ia para o namespace
   * errado e simplesmente não chegava a ninguém, sem erro nenhum).
   */
  events?: ContractEvents;
  /** Presente só quando a leitura é cacheada no frontend. */
  cache?: ContractCache;
}

/**
 * `TParams`/`TResponse`/`TEventPayload` são tipos fantasma: existem só em
 * compile-time (TS apaga generics no build), então os campos
 * `_params`/`_response`/`_eventPayload` nunca têm valor em runtime — servem
 * para o editor/tsc inferirem o shape ao usar o contrato
 * (`typeof queueContract._eventPayload`), não para desestruturar.
 */
export interface Contract<TParams = void, TResponse = unknown, TEventPayload = unknown>
  extends ContractDef<TParams, TResponse, TEventPayload> {
  readonly _params?: TParams;
  readonly _response?: TResponse;
  readonly _eventPayload?: TEventPayload;
}

export const defineContract = <TParams = void, TResponse = unknown, TEventPayload = unknown>(
  def: ContractDef<TParams, TResponse, TEventPayload>
): Contract<TParams, TResponse, TEventPayload> => def;

/**
 * `io.emit(nome, payload)` do socket.io não sabe nada sobre
 * `TEventPayload` — `payload` ali é `any`. Passar o literal por aqui força
 * o TS a validar contra o tipo do contrato ANTES do emit (`typedPayload`
 * só devolve o mesmo objeto, o valor de retorno é o que vai pro `.emit`).
 * Sem isso, `QueueEventPayload` etc. seriam só documentação — nada
 * quebraria se alguém escrevesse `{ action: "updat", queeu }`.
 */
export const typedPayload = <TEventPayload>(
  _contract: Contract<any, any, TEventPayload>,
  payload: TEventPayload
): TEventPayload => payload;

/**
 * Mesma ideia de `typedPayload`, para evento sem Contract associado (sem
 * rota GET, ex: SOCKET_EVENTS.TICKET.TRANSFER — só progresso de um job).
 * Sem `_contract` pra inferir o tipo, o generic é explícito no call site:
 * `typedEvent<TicketTransferEventPayload>({ action: "progress", ... })`.
 */
export const typedEvent = <TEventPayload>(payload: TEventPayload): TEventPayload => payload;
